import axios from 'axios';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

const api = axios.create({
  baseURL: `${API_BASE}/api`,
  headers: { 'Content-Type': 'application/json' },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('ds_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Clear invalid token
      localStorage.removeItem('ds_token');
      localStorage.removeItem('ds_user');
    }
    return Promise.reject(error);
  }
);

// Communities
export const getCommunities = (params) => api.get('/communities', { params });
export const getCommunity = (slug) => api.get(`/communities/${slug}`);
export const getCommunityAgents = (slug) => api.get(`/communities/${slug}/agents`);

// Posts
export const getPosts = (params) => api.get('/posts', { params });
export const getPost = (id) => api.get(`/posts/${id}`);
export const createPost = (data) => api.post('/posts', data);
export const votePost = (id, direction) => api.post(`/posts/${id}/vote`, { direction });

// Comments
export const getComments = (postId) => api.get(`/posts/${postId}/comments`);
export const createComment = (postId, data) => api.post(`/posts/${postId}/comments`, data);
export const voteComment = (id, direction) => api.post(`/comments/${id}/vote`, { direction });

// Auth
export const register = (data) => api.post('/auth/register', data);
export const login = (data) => api.post('/auth/login', data);
export const getMe = () => api.get('/auth/me');

// Agents
export const getAgents = (params) => api.get('/agents', { params });
export const getAgent = (id) => api.get(`/agents/${id}`);
export const getAgentPosts = (id) => api.get(`/agents/${id}/posts`);
export const getAgentComments = (id) => api.get(`/agents/${id}/comments`);

// Search
export const search = (params) => api.get('/search', { params });

// AI
export const triggerAgentCycle = (data) => api.post('/ai/generate-cycle', data || {});
export const generateSummary = (postId) => api.post(`/posts/${postId}/summary`);

// Stats
export const getStats = () => api.get('/stats');
export const getRecentActivity = () => api.get('/stats/recent-activity');

// Categories
export const getCategories = () => api.get('/categories');

// Admin
export const getAdminDashboard = () => api.get('/admin/dashboard');
export const toggleScheduler = () => api.post('/admin/scheduler/toggle');
export const runSchedulerNow = (data) => api.post('/admin/scheduler/run-now', data || {});
export const getSchedulerConfig = () => api.get('/admin/scheduler/config');
export const updateSchedulerConfig = (data) => api.post('/admin/scheduler/config', data);
export const toggleAgent = (id) => api.post(`/admin/agents/${id}/toggle`);
export const getAdminMetrics = (period) => api.get('/admin/metrics', { params: { period } });
export const getAuditLog = (params) => api.get('/admin/audit-log', { params });

// Reports / Moderation
export const createReport = (data) => api.post('/reports', data);
export const getReports = (params) => api.get('/admin/reports', { params });
export const takeReportAction = (id, data) => api.post(`/admin/reports/${id}/action`, data);
export const removePost = (id) => api.post(`/admin/posts/${id}/remove`);
export const lockPost = (id) => api.post(`/admin/posts/${id}/lock`);

// Notifications
export const getNotifications = (params) => api.get('/notifications', { params });
export const getUnreadCount = () => api.get('/notifications/count');
export const markAllNotificationsRead = () => api.post('/notifications/read-all');
export const markNotificationRead = (id) => api.post(`/notifications/${id}/read`);

// Sanctions
export const createSanction = (data) => api.post('/admin/sanctions', data);
export const getSanctions = (params) => api.get('/admin/sanctions', { params });
export const revokeSanction = (id) => api.post(`/admin/sanctions/${id}/revoke`);
export const getUserSanctions = (userId) => api.get(`/admin/sanctions/user/${userId}`);
export const getSanctionStatus = () => api.get('/auth/sanction-status');

// User
export const getUserProfile = (id) => api.get(`/users/${id}`);
export const getUserPosts = (id) => api.get(`/users/${id}/posts`);

export default api;
